// src/Pages/InquiryDetails_v1.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AiOutlinePlus } from "react-icons/ai";
import Snackbar from '@mui/material/Snackbar';
import { Container, Paper, Box, Grid, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Card, CardContent, Button, Checkbox, CircularProgress, } from "@mui/material";

// MUI icons for priority in mobile cards
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
// Custom components for inputs, dropdowns, etc.
import FloatingLabelInput from "../components/FloatingLabelInput";
import DateInput from "../components/DateInput";
import Dropdown from "../components/Dropdown";
import FormattedDate from "../components/FormattedDate";

// const path = "http://103.251.218.254:3100";


// Helper function to split records for team leader.
const splitRecordsForTeamLeader = (records, userId) => {
    const selfRecords = records.filter((record) => record.userId === userId);
    const teamRecords = records.filter((record) => record.userId !== userId);
    return { self: selfRecords, team: teamRecords };
};

// Helper: return color based on priority
const getPriorityColor = (priority) => {
    if (!priority) return "inherit";
    const p = priority.toLowerCase();
    if (p === "high") return "red";
    if (p === "medium") return "orange";
    if (p === "low") return "green";
    return "inherit";
};

// Helper: render priority icon in mobile view
const renderPriorityIcon = (priority) => {
    const color = getPriorityColor(priority);
    if (!priority) return null;
    const p = priority.toLowerCase();
    if (p === "high") return <PriorityHighIcon sx={{ color, mr: 1 }} />;
    if (p === "medium") return <ReportProblemIcon sx={{ color, mr: 1 }} />;
    if (p === "low") return <LowPriorityIcon sx={{ color, mr: 1 }} />;
    return null;
};

const InquiryDetails_v1 = ({ loggedInUser, connectionString }) => {
    const path = connectionString;

    // ----------------------- State Variables -----------------------
    const [inquiries, setInquiries] = useState([]);
    const [filters, setFilters] = useState({
        inquiryDate: "",
        status: "",
        priority: "",
        clientname: "",
        clientContact: "",
        mobileNo: "",
    });
    const [totalRecords, setTotalRecords] = useState(0);
    const [displayedRecords, setDisplayedRecords] = useState(0);
    const batch = 10;
    const navigate = useNavigate();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [successMessage, setSuccessMessage] = useState('');

    // State to track loading until records are received.
    const [isLoading, setIsLoading] = useState(false);

    // Dropdown options for filtering.
    const [statusOptions, setStatusOptions] = useState([]);
    const [priorityOptions, setPriorityOptions] = useState([]);

    // State for selected checkbox IDs.
    const [selectedIds, setSelectedIds] = useState([]);

    // ----------------------- Data Fetching and Side Effects -----------------------
    useEffect(() => {
        axios.get(`${path}/api/status`)
            .then((res) => {
                if (res.data.success && Array.isArray(res.data.data)) {
                    setStatusOptions(res.data.data);
                } else {
                    setStatusOptions([]);
                }
            })
            .catch((error) => console.error("Error fetching status:", error));

        axios.get(`${path}/api/priority`)
            .then((res) => {
                if (res.data.success && Array.isArray(res.data.data)) {
                    setPriorityOptions(res.data.data);
                } else {
                    setPriorityOptions([]);
                }
            })
            .catch((error) => console.error("Error fetching priority options:", error));
        fetchInquiries(false);
    }, []);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ----------------------- Function: fetchInquiries -----------------------
    /**
     * fetchInquiries
     * ----------------
     * Fetches inquiries from the backend based on current filter values.
     */
    const fetchInquiries = async (isLoadMore = false, overrideFilters = null) => {
        setIsLoading(true);
        try {
            const usedFilters = overrideFilters !== null ? overrideFilters : filters;
            let offset = isLoadMore ? Array.isArray(inquiries) ? inquiries.length : (inquiries.self || []).length + (inquiries.team || []).length : 0;

            const response = await axios.get(`${path}/api/inquiries`, { params: { ...usedFilters, limit: batch, offset, userId: loggedInUser.userId, companyId: loggedInUser.companyId, role: loggedInUser.userCategory, }, });

            let fetchedRecords = response.data.records || [];

            if (loggedInUser.userCategory === "Team Leader") {
                let { self, team } = splitRecordsForTeamLeader(fetchedRecords, loggedInUser.userId);
                setInquiries((prev) => ({ self: isLoadMore ? [...(prev?.self || []), ...self] : self, team: isLoadMore ? [...(prev?.team || []), ...team] : team, }));
            } else {
                setInquiries(isLoadMore ? [...inquiries, ...fetchedRecords] : fetchedRecords);
            }

            setDisplayedRecords(isLoadMore ? displayedRecords + fetchedRecords.length : fetchedRecords.length);
            setTotalRecords(response.data.total);
        } catch (error) {
            console.error("Error fetching inquiries:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // ----------------------- Event Handlers -----------------------
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({ ...filters, [name]: value });
    };

    const handleSearch = async () => {
        await fetchInquiries(false);
        setSelectedIds([]);
    };

    const handleResetFilters = async () => {
        const emptyFilters = {
            inquiryDate: "",
            status: "",
            priority: "",
            clientname: "",
            clientContact: "",
            mobileNo: "",
        };
        await fetchInquiries(false, emptyFilters);
        setFilters(emptyFilters);
        setSelectedIds([]);
    };

    const handleAddBtn = () => {
        console.log("Mode: Insert");
        navigate("/main/v1/inquiry", { state: { mode: "insert" } });
    };

    const handleRowClick = (inquiry) => {
        console.log(inquiry);
        navigate("/main/v1/inquiry", { state: { mode: "edit", inquiryData: { inquiry } } });
    };

    const handleSelectAll = (e, data) => {
        if (e.target.checked) {
            const allIds = data.map((inquiry) => inquiry.inquiryId);
            console.log('In handleSelectAll:', allIds);
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleRowCheckboxChange = (e, id) => {
        e.stopPropagation();
        if (e.target.checked) {
            setSelectedIds((prev) => [...prev, id]);
        } else {
            setSelectedIds((prev) => prev.filter((item) => item !== id));
        }
    };

    const handleDelete = async () => {
        if (selectedIds.length === 0) {
            alert("Please select at least one record.");
        } else {
            try {
                const res = await axios.post(`${path}/api/followupCount`, { inquiryIds: selectedIds });
                const followupCounts = {};
                const followupArray = [];
                const invalidIds = [];
                const validIds = [];

                res.data.forEach((item) => {
                    followupCounts[item.inqid] = item.count;
                    followupArray.push({ id: item.inqid, count: item.count });
                    if (item.count === 1) {
                        validIds.push(item.inqid);
                    } else {
                        invalidIds.push(item.inqid);
                    }
                });

                console.log("Valid IDs for deletion:", validIds);
                console.log("Invalid IDs (Count > 1):", invalidIds);

                if (invalidIds.length > 0) {
                    setSuccessMessage(`Cannot delete: ${invalidIds.join(", ")} (More than 1 follow-up)`);
                } else {
                    const deleteResponse = await axios.post(`${path}/api/deleteInquiries`, { objAryId: validIds });
                    if (deleteResponse.status === 200) {
                        setSuccessMessage("Inquiries deleted successfully!");
                        setInquiries((prevInquiries) => prevInquiries.filter((inquiry) => !validIds.includes(inquiry.inquiryId)));
                        setSelectedIds([]);
                    } else {
                        setSuccessMessage("Failed to delete inquiries.");
                    }
                    console.log("Deletion response:", deleteResponse.data);
                }
            } catch (error) {
                console.error("Error during deletion:", error);
                setSuccessMessage("Error deleting inquiries.");
            }
        }
    };

    // ----------------------- Render Functions -----------------------
    const HL_FN_RenderDesktopTable = (data) => (
        <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
            <Table>
                <TableHead sx={{ backgroundColor: "grey.300" }}>
                    <TableRow>
                        <TableCell align="center">
                            <Checkbox onChange={(e) => handleSelectAll(e, data)} checked={data.length > 0 && data.every((inquiry) => selectedIds.includes(inquiry.inquiryId))} />
                        </TableCell>
                        <TableCell>#</TableCell>
                        <TableCell>Inq. No</TableCell>
                        <TableCell>Inq. Date</TableCell>
                        <TableCell>Follow-Up Date</TableCell>
                        <TableCell>Client Name</TableCell>
                        <TableCell>Contact Person</TableCell>
                        <TableCell>Mobile No</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Assign</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((inquiry, index) => {
                        const priorityColor = getPriorityColor(inquiry.priority);
                        return (
                            <TableRow key={inquiry.inquiryId} hover onClick={() => handleRowClick(inquiry)} sx={{ backgroundColor: index % 2 === 0 ? "white" : "grey.50", cursor: "pointer", }} >
                                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                    <Checkbox value={inquiry.inquiryId} onChange={(e) => handleRowCheckboxChange(e, inquiry.inquiryId)} checked={selectedIds.includes(inquiry.inquiryId)} />
                                </TableCell>
                                <TableCell align="center">{index + 1}</TableCell>
                                <TableCell>{inquiry.inquiryNumber}</TableCell>
                                <TableCell>
                                    <FormattedDate dateString={inquiry.inquiryDate} />
                                </TableCell>
                                <TableCell>
                                    <FormattedDate dateString={inquiry.nextFollowUpDate} />
                                </TableCell>
                                <TableCell>{inquiry.clientname}</TableCell>
                                <TableCell>{inquiry.clientContact}</TableCell>
                                <TableCell>{inquiry.mobileNo}</TableCell>
                                <TableCell>{inquiry.statusName}</TableCell>
                                <TableCell sx={{ color: priorityColor, fontWeight: "bold" }}>
                                    {inquiry.priority}
                                </TableCell>
                                <TableCell>{inquiry.employeeName || "Unassigned"}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // Mobile Card View with responsive icon and colored priority display.
    const HL_FN_RenderMobileCard = (data) => (
        <Box sx={{ px: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, mr: 1 }}>
                <Checkbox onChange={(e) => handleSelectAll(e, data)} checked={data.length > 0 && data.every((inquiry) => selectedIds.includes(inquiry.inquiryId))} />
                <Typography variant="subtitle1">Select All</Typography>
            </Box>

            {data.map((inquiry, index) => (
                <Card key={inquiry.inquiryId} variant="outlined" sx={{ cursor: "pointer", boxShadow: 3, borderRadius: 2, position: "relative", mb: 2, p: 2, transition: "0.3s", "&:hover": { boxShadow: 6, }, }} onClick={() => handleRowClick(inquiry)} >
                    {/* Header Row */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Checkbox sx={{ mr: 1 }} value={inquiry.inquiryId} onClick={(e) => e.stopPropagation()} onChange={(e) => handleRowCheckboxChange(e, inquiry.inquiryId)} checked={selectedIds.includes(inquiry.inquiryId)} />
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                            {/* <PersonIcon sx={{ fontSize: 16, mr: 1, color: "gray" }} /> | */}
                            #{index + 1} -  {inquiry.clientname}
                        </Typography>
                    </Box>

                    {/* Details Section */}
                    <CardContent sx={{ px: 0, pt: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <PersonIcon sx={{ fontSize: 16, mr: 1, color: "gray" }} />
                            <Typography variant="body2">
                                <strong>Contact:</strong> {inquiry.clientContact}
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <PersonIcon sx={{ fontSize: 16, mr: 1, color: "gray" }} />
                            <Typography variant="body2">
                                <strong>Assign:</strong> {inquiry.employeeName || "Unassigned"}
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5, fontWeight: "bold", }} >
                            {renderPriorityIcon(inquiry.priority)}
                            <Typography sx={{ ml: 1 }}>
                                <strong>Priority:</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ ml: 1, color: getPriorityColor(inquiry.priority), fontWeight: "bold", }} >
                                {inquiry.priority}
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 16, mr: 1, color: "gray" }} />
                            <Typography variant="body2">
                                <strong>Mobile:</strong> {inquiry.mobileNo}
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <Typography variant="body2">
                                <strong>Status:</strong> {inquiry.statusName}
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: "gray" }} />
                            <Typography variant="body2">
                                <strong>Inq. Date:</strong> <FormattedDate dateString={inquiry.inquiryDate} />
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: "gray" }} />
                            <Typography variant="body2">
                                <strong>Follow-Up:</strong> <FormattedDate dateString={inquiry.nextFollowUpDate} />
                            </Typography>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                            <Typography variant="body2">
                                <strong>Inq. No: {inquiry.inquiryNumber}</strong>
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );

    // FN_RenderTable returns table or card view based on windowWidth.
    const FN_RenderTable = (data) => {
        if (!data || data.length === 0) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 150, }} >
                    <Typography variant="h5" color="text.secondary">
                        No records found.
                    </Typography>
                </Box>
            );
        }
        return windowWidth < 1024 ? HL_FN_RenderMobileCard(data) : HL_FN_RenderDesktopTable(data);
    };

    let mergedInquiries = [];
    if (loggedInUser.userCategory === "Team Leader") {
        mergedInquiries = [...(inquiries.self || []), ...(inquiries.team || [])];
    }

    return (
        <Container sx={{ py: 3 }}>
            {/* Page Heading */}
            <Typography variant="h4" align="center" gutterBottom sx={{ textDecoration: "underline" }} >
                Inquiry Details
            </Typography>

            {/* Search Section */}
            <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Search Filters
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={2}>
                        <DateInput label="Inq. Date" name="inquiryDate" value={filters.inquiryDate} onChange={(e) => handleFilterChange({ target: { name: "inquiryDate", value: e.target.value }, })} sx={{ width: "100%" }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FloatingLabelInput label="Inq. No" name="inquiryNumber" value={filters.inquiryNumber || ""} onChange={handleFilterChange} sx={{ width: "100%" }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Dropdown label="Status" name="status" value={filters.status} options={statusOptions} valueKey="LeadStatus" labelKey="LeadStatus" onChange={(selectedOption) => handleFilterChange({ target: { name: "status", value: selectedOption.LeadStatus }, })} sx={{ width: "100%" }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Dropdown label="Priority" name="priority" value={filters.priority} options={priorityOptions} valueKey="value" labelKey="value" onChange={(selectedOption) => handleFilterChange({ target: { name: "priority", value: selectedOption.value }, })} sx={{ width: "100%" }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FloatingLabelInput label="Client Name" name="clientname" value={filters.clientname} onChange={handleFilterChange} sx={{ width: "100%" }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FloatingLabelInput label="Contact Person" name="clientContact" value={filters.clientContact} onChange={handleFilterChange} sx={{ width: "100%" }} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FloatingLabelInput label="Mobile No" name="mobileNo" value={filters.mobileNo} onChange={handleFilterChange} sx={{ width: "100%" }} />
                    </Grid>
                </Grid>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 2 }}>
                    <Button variant="contained" color="primary" onClick={handleSearch}> Search </Button>
                    <Button variant="contained" color="secondary" onClick={handleResetFilters}> Reset </Button>
                </Box>
            </Paper>

            {/* Top Section: Add Button (left), Count (center), Delete Button (right) */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Button variant="contained" color="success" onClick={handleAddBtn} startIcon={<AiOutlinePlus size={18} />} > Add </Button>
                <Typography variant="body1">
                    {loggedInUser.userCategory === "Team Leader" ? `Total: ${(inquiries.self || []).length + (inquiries.team || []).length} / ${totalRecords}` : `Count: ${displayedRecords} / ${totalRecords}`}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="contained" color="error" onClick={handleDelete}> Delete </Button>
                    {displayedRecords < totalRecords && (<Button variant="contained" color="primary" onClick={() => fetchInquiries(true)}> Show More </Button>)}
                </Box>
            </Box>

            {/* Render Inquiries or a Loading Indicator */}
            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200, }} >
                    <CircularProgress />
                </Box>
            ) : loggedInUser.userCategory === "Team Leader" ? (
                <Box>{FN_RenderTable([...(inquiries.self || []), ...(inquiries.team || [])])}</Box>
            ) : (
                <Box>{FN_RenderTable(inquiries)}</Box>
            )}

            <Snackbar open={!!successMessage} autoHideDuration={null} onClose={() => setSuccessMessage("")} message={successMessage} action={<button onClick={() => setSuccessMessage("")} style={{ color: "white" }}> Close </button>} />
        </Container>
    );
};

export default InquiryDetails_v1;